import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { formatPhone } from '../components/calendar/AppointmentPanel';
import { ClientImport } from '../components/ClientImport';
import { SidePanel, Switch, TextArea, TitleBlock } from '../components/forms';
import { Button, EmptyState, ErrorNote, Field } from '../components/ui';
import { api, ApiError, type Salon } from '../lib/api';
import { useLocale, useT, type TKey } from '../lib/i18n';
import { dateOf, formatDateShort, formatStamp, formatTime, SALON_TZ } from '../lib/time';

type ClientRow = {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  alertNote: string | null;
  visits: number;
  lastVisit: string | null;
  nextVisit: string | null;
  isNew: boolean;
};
type Group = 'all' | 'upcoming' | 'away' | 'new';
type ClientPage = { clients: ClientRow[]; groups: Record<Group, number> };
const PAGE = 50;
const GROUPS: Group[] = ['all', 'upcoming', 'away', 'new'];
type ClientDetail = {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  alertNote: string | null;
  alertHealth: boolean;
  bookingBlocked: boolean;
  blockedReason: string | null;
  notes: string | null;
  createdAt: string;
  noShows: number;
  lateCancels: number;
  messages: Array<{ kind: string; toAddress: string; status: string; sentAt: string }>;
  appointments: Array<{ id: string; startsAt: string; status: string; source: string; service: string; priceCents: number; active: boolean }>;
};

const fullName = (c: { firstName: string; lastName: string | null }) => [c.firstName, c.lastName].filter(Boolean).join(' ');
const errorOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);
const fetchClients = (orgId: string, q: string, group: Group, offset: number) => api<ClientPage>(`/orgs/${orgId}/clients?${new URLSearchParams({ q, group, offset: String(offset) })}`);

/** Wide screens show the client card next to the list instead of over it. */
function useIsWide() {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(min-width: 1280px)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(min-width: 1280px)').matches,
  );
}

/** Klanten: everyone who booked or was booked, in groups, with search, profile and privacy actions. */
export function ClientsPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const locale = useLocale();
  const wide = useIsWide();
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [group, setGroup] = useState<Group>('all');
  // ?klant=<id> opens that client (links from Vandaag → Te doen).
  const { klant } = useSearch({ from: '/s/$orgId/klanten' });
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | 'new' | null>(klant ?? null);
  const [importing, setImporting] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(id);
  }, [q]);
  const list = useInfiniteQuery({
    queryKey: ['clients', orgId, debounced, group],
    queryFn: ({ pageParam }) => fetchClients(orgId, debounced, group, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.clients.length === PAGE ? pages.length * PAGE : undefined),
    placeholderData: (p) => p,
  });
  const rows = list.data?.pages.flatMap((p) => p.clients) ?? [];
  const groups = list.data?.pages[0]?.groups;
  const date = (iso: string | null) => (iso ? formatDateShort(dateOf(iso, SALON_TZ), locale) : '—');
  const closeClient = () => {
    setOpen(null);
    if (klant) void navigate({ to: '/s/$orgId/klanten', params: { orgId }, search: {}, replace: true });
  };
  const viewing = open && open !== 'new' ? open : null;

  return (
    <div className="flex w-full flex-col gap-5">
      <TitleBlock
        title={t('nav.clients')}
        count={groups?.all ?? null}
        action={
          <>
            {salon.data && salon.data.role !== 'staff' && (
              <button type="button" onClick={() => setImporting(true)} className="hidden min-h-11 items-center rounded-full border border-line bg-surface px-5 text-[15px] font-medium hover:bg-subtle sm:inline-flex">
                {t('import.button')}
              </button>
            )}
            {salon.data?.role === 'owner' && (
              <a href={`/api/v1/orgs/${orgId}/clients/export.csv`} className="hidden min-h-11 items-center rounded-full border border-line bg-surface px-5 text-[15px] font-medium hover:bg-subtle sm:inline-flex">
                {t('clients.export')}
              </a>
            )}
            <Button onClick={() => setOpen('new')}>+ {t('appt.newClient')}</Button>
          </>
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          aria-label={t('appt.searchClient')}
          placeholder={t('appt.searchClient')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="min-h-12 w-full max-w-md flex-1 rounded-full border border-line bg-surface px-5 text-base shadow-[0_1px_1px_rgb(2_53_42/0.04)] outline-none"
        />
        <div role="tablist" aria-label={t('nav.clients')} className="flex max-w-full overflow-x-auto rounded-full border border-line bg-surface p-0.5 shadow-[0_1px_1px_rgb(2_53_42/0.04)]">
          {GROUPS.map((g) => (
            <button
              key={g}
              role="tab"
              aria-selected={group === g}
              onClick={() => setGroup(g)}
              className={`flex min-h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-medium ${group === g ? 'bg-ink text-canvas' : 'text-muted hover:text-ink'}`}
            >
              {t(`clients.group.${g}`)}
              {groups && <span className={`tabular-nums ${group === g ? 'text-canvas/60' : 'text-muted/70'}`}>{groups[g]}</span>}
            </button>
          ))}
        </div>
      </div>
      {group !== 'all' && <p className="-mt-2 text-[15px] text-muted">{t(`clients.groupHint.${group}`)}</p>}

      <div className="flex flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,1fr)_420px] xl:items-start">
        <div className="flex min-w-0 flex-col gap-3">
          {list.data && rows.length === 0 ? (
            debounced ? (
              <p className="text-muted">{t('appt.noResults')}</p>
            ) : group !== 'all' ? (
              <p className="rounded-[var(--radius-card)] border border-dashed border-line px-5 py-8 text-center text-muted">{t('clients.groupEmpty')}</p>
            ) : (
              <EmptyState title={t('clients.empty.title')} body={t('clients.empty.body')} />
            )
          ) : (
            <div className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[0_1px_2px_rgb(18_19_22/0.03)]">
              <table className="w-full text-left">
                <thead className="hidden border-b border-line sm:table-header-group [&_th]:eyebrow [&_th]:px-4 [&_th]:py-2.5 [&_th]:font-medium">
                  <tr>
                    <th>{t('clients.name')}</th>
                    <th>{t('appt.phone')}</th>
                    <th>{t('clients.lastVisit')}</th>
                    <th>{t('clients.nextVisit')}</th>
                    <th className="text-right">{t('clients.visits')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setOpen(c.id)}
                      aria-selected={viewing === c.id}
                      className={`cursor-pointer border-b border-line last:border-b-0 ${viewing === c.id ? 'bg-accent-soft/50' : 'hover:bg-subtle'}`}
                    >
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-3">
                          <Initials name={fullName(c)} />
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <button type="button" className="text-left font-medium" onClick={() => setOpen(c.id)}>
                                {fullName(c)}
                              </button>
                              {c.alertNote && <span className="tag bg-warning-soft text-warning">{t('cal.alert')}</span>}
                              {c.isNew && <span className="tag bg-accent-soft text-accent-strong">{t('tag.new')}</span>}
                            </span>
                            <span className="block truncate text-sm text-muted sm:hidden">{c.phone ? formatPhone(c.phone) : (c.email ?? '')}</span>
                            {c.email && <span className="hidden truncate text-[13px] text-muted sm:block">{c.email}</span>}
                          </span>
                        </span>
                      </td>
                      <td className="hidden px-4 py-2.5 tabular-nums text-muted sm:table-cell">{c.phone ? formatPhone(c.phone) : '—'}</td>
                      <td className="hidden px-4 py-2.5 text-muted sm:table-cell">{date(c.lastVisit)}</td>
                      <td className={`hidden px-4 py-2.5 sm:table-cell ${c.nextVisit ? 'font-medium' : 'text-muted'}`}>{date(c.nextVisit)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-muted">{c.visits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > 0 && groups && (
            <div className="flex items-center justify-between gap-3 px-1 text-sm text-muted">
              <span className="tabular-nums">{t('clients.shown', { n: rows.length, total: debounced ? rows.length : groups[group] })}</span>
              {list.hasNextPage && (
                <Button variant="secondary" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                  {t('clients.more')}
                </Button>
              )}
            </div>
          )}
        </div>

        {wide && (
          <div className="sticky top-4 flex flex-col gap-4">
            {viewing ? (
              <DockedClient key={viewing} orgId={orgId} id={viewing} manager={salon.data?.role !== 'staff'} onClose={closeClient} />
            ) : (
              <>
                {groups && groups.all > 0 && <Overview groups={groups} active={group} onGroup={setGroup} />}
                {groups && groups.away > 0 && group !== 'away' && <CallBack orgId={orgId} total={groups.away} onOpen={setOpen} onAll={() => setGroup('away')} />}
                <div className="rounded-[var(--radius-card)] border border-dashed border-line px-5 py-5">
                  <p className="display text-[22px] leading-tight">{t('clients.pickTitle')}</p>
                  <p className="mt-1 text-[14px] text-muted">{t('clients.pickBody')}</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {importing && <ClientImport orgId={orgId} onClose={() => setImporting(false)} />}
      {open === 'new' && <NewClientPanel orgId={orgId} onClose={() => setOpen(null)} onCreated={(id) => setOpen(id)} />}
      {viewing && !wide && <ClientPanel orgId={orgId} id={viewing} manager={salon.data?.role !== 'staff'} onClose={closeClient} />}
    </div>
  );
}

/** How the client base stands: who has a next appointment booked, who hasn't, who's been away. */
function Overview({ groups, active, onGroup }: { groups: Record<Group, number>; active: Group; onGroup: (g: Group) => void }) {
  const t = useT();
  const share = (n: number) => (groups.all ? (n / groups.all) * 100 : 0);
  const unplanned = groups.all - groups.upcoming - groups.away;
  // The first three add up to everyone (the bar); "new" is a separate count below them.
  const rows: Array<{ group: Group | null; label: string; n: number; swatch: string }> = [
    { group: 'upcoming', label: t('clients.group.upcoming'), n: groups.upcoming, swatch: 'bg-ink' },
    { group: null, label: t('clients.nothingPlanned'), n: unplanned, swatch: 'bg-line' },
    { group: 'away', label: t('clients.group.away'), n: groups.away, swatch: 'bg-warning' },
    { group: 'new', label: t('clients.group.new'), n: groups.new, swatch: 'bg-accent' },
  ];
  return (
    <section className="rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(2_53_42/0.04)]">
      <h2 className="eyebrow">{t('clients.overview')}</h2>
      <p className="mt-3 flex items-baseline gap-2">
        <span className="display text-[44px] leading-none tabular-nums">{Math.round(share(groups.upcoming))}%</span>
        <span className="text-[15px] text-muted">{t('clients.rebooked')}</span>
      </p>
      <div aria-hidden className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-subtle">
        <span className="bg-ink" style={{ width: `${share(groups.upcoming)}%` }} />
        <span className="bg-line" style={{ width: `${share(unplanned)}%` }} />
        <span className="bg-warning" style={{ width: `${share(groups.away)}%` }} />
      </div>
      <ul className="mt-4 flex flex-col">
        {rows.map((r) => {
          const inner = (
            <>
              <span className={`size-2.5 shrink-0 rounded-full ${r.swatch}`} />
              <span className="flex-1">{r.label}</span>
              <span className="tabular-nums text-muted">{r.n}</span>
            </>
          );
          const row = 'flex w-full items-center gap-3 rounded-[10px] px-2 py-1.5 text-left text-[15px]';
          const group = r.group;
          return (
            <li key={r.label} className={group === 'new' ? 'mt-1.5 border-t border-line pt-1.5' : ''}>
              {group ? (
                <button type="button" onClick={() => onGroup(group)} className={`${row} hover:bg-subtle ${active === group ? 'bg-subtle font-medium' : ''}`}>
                  {inner}
                </button>
              ) : (
                <span className={row}>{inner}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** "Stuur herinnering": one email inviting the client to book again; the API says why when it can't. */
function RemindButton({ orgId, clientId, compact = false }: { orgId: string; clientId: string; compact?: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const remind = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/clients/${clientId}/rebook-reminder`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['client', orgId, clientId] }),
  });
  if (remind.isSuccess) return <span className="shrink-0 text-sm font-medium text-success">✓ {t('clients.reminded')}</span>;
  return (
    <span className={`flex flex-col ${compact ? 'items-end' : 'items-start'} gap-1`}>
      <button type="button" onClick={() => remind.mutate()} disabled={remind.isPending} title={t('clients.remindHint')} className="shrink-0 rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle">
        {t('clients.remind')}
      </button>
      {remind.error && <span className="max-w-[16rem] text-xs text-danger">{remind.error instanceof ApiError ? remind.error.message : t('error.generic')}</span>}
    </span>
  );
}

/** "Even bellen?": regulars who haven't been in for a while and have nothing planned. */
function CallBack({ orgId, total, onOpen, onAll }: { orgId: string; total: number; onOpen: (id: string) => void; onAll: () => void }) {
  const t = useT();
  const away = useQuery({ queryKey: ['clients', orgId, '', 'away', 'preview'], queryFn: () => fetchClients(orgId, '', 'away', 0) });
  const people = away.data?.clients.slice(0, 5) ?? [];
  if (people.length === 0) return null;
  const weeks = (iso: string | null) => (iso ? Math.floor((Date.now() - Date.parse(iso)) / (7 * 86_400_000)) : 0);
  return (
    <section className="rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(2_53_42/0.04)]">
      <h2 className="display text-[26px] leading-tight">{t('clients.callBack')}</h2>
      <p className="mt-0.5 text-[15px] text-muted">{t('clients.callBackBody')}</p>
      <ul className="mt-3 flex flex-col">
        {people.map((c) => (
          <li key={c.id} className="flex items-center gap-3 border-t border-line py-2.5 first:border-t-0">
            <Initials name={fullName(c)} />
            <button type="button" onClick={() => onOpen(c.id)} className="min-w-0 flex-1 text-left">
              <span className="block truncate font-medium">{fullName(c)}</span>
              <span className="block text-[13px] text-muted">
                {t('clients.weeksAgo', { n: weeks(c.lastVisit) })} · {t('clients.visitsN', { n: c.visits })}
              </span>
            </button>
            {c.email ? (
              <RemindButton orgId={orgId} clientId={c.id} compact />
            ) : (
              c.phone && (
                <a href={`tel:${c.phone}`} className="shrink-0 rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle">
                  {t('appt.call')}
                </a>
              )
            )}
          </li>
        ))}
      </ul>
      {total > people.length && (
        <button type="button" onClick={onAll} className="mt-2 text-sm font-semibold text-accent-strong hover:underline">
          {t('clients.seeAll', { n: total })}
        </button>
      )}
    </section>
  );
}

/** The client card beside the list on wide screens. */
function DockedClient({ orgId, id, manager, onClose }: { orgId: string; id: string; manager: boolean; onClose: () => void }) {
  const t = useT();
  const detail = useClient(orgId, id);
  return (
    <section aria-label={detail.data ? fullName(detail.data) : undefined} className="flex max-h-[calc(100dvh-32px)] flex-col overflow-hidden rounded-[var(--radius-card)] border border-line bg-canvas shadow-[0_12px_40px_rgb(2_53_42/0.10)]">
      <div className="relative flex items-start justify-between gap-3 border-b border-line bg-surface px-5 py-4">
        <span className="relative flex min-w-0 items-center gap-3">
          {detail.data && <Initials name={fullName(detail.data)} size="lg" />}
          <h2 className="display truncate text-[28px] leading-tight">{detail.data ? fullName(detail.data) : t('common.loading')}</h2>
        </span>
        <button onClick={onClose} className="relative flex size-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-subtle hover:text-ink" aria-label={t('common.close')}>
          ✕
        </button>
      </div>
      <div className="overflow-y-auto px-5 py-4">
        <ClientBody orgId={orgId} id={id} manager={manager} onClose={onClose} />
      </div>
    </section>
  );
}

const useClient = (orgId: string, id: string) =>
  useQuery({ queryKey: ['client', orgId, id], queryFn: () => api<{ client: ClientDetail }>(`/orgs/${orgId}/clients/${id}`).then((r) => r.client) });

function NewClientPanel({ orgId, onClose, onCreated }: { orgId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const t = useT();
  const qc = useQueryClient();
  return (
    <SidePanel open title={t('appt.newClient')} onClose={onClose}>
      <ClientForm
        orgId={orgId}
        onSaved={(c) => {
          void qc.invalidateQueries({ queryKey: ['clients', orgId] });
          onCreated(c.id);
        }}
        onCancel={onClose}
      />
    </SidePanel>
  );
}

function ClientPanel({ orgId, id, manager, onClose }: { orgId: string; id: string; manager: boolean; onClose: () => void }) {
  const t = useT();
  const detail = useClient(orgId, id);
  return (
    <SidePanel open title={detail.data ? fullName(detail.data) : t('common.loading')} onClose={onClose}>
      <ClientBody orgId={orgId} id={id} manager={manager} onClose={onClose} />
    </SidePanel>
  );
}

function ClientBody({ orgId, id, manager, onClose }: { orgId: string; id: string; manager: boolean; onClose: () => void }) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const key = ['client', orgId, id];
  const detail = useClient(orgId, id);
  const [editing, setEditing] = useState(false);
  const refresh = (c?: ClientDetail) => {
    if (c) qc.setQueryData(key, c);
    void qc.invalidateQueries({ queryKey: ['clients', orgId] });
  };
  const block = useMutation({
    mutationFn: (blocked: boolean) => api<{ client: ClientDetail }>(`/orgs/${orgId}/clients/${id}/blocked`, { method: 'PUT', body: JSON.stringify({ blocked }) }).then((r) => r.client),
    onSuccess: refresh,
  });
  const anonymise = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/clients/${id}/anonymise`, { method: 'POST' }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const c = detail.data;

  return (
    <>
      {!c ? (
        <ErrorNote message={errorOf(detail.error, t('error.generic'))} />
      ) : editing ? (
        <ClientForm
          orgId={orgId}
          client={c}
          onSaved={(saved) => {
            refresh(saved as ClientDetail);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="flex flex-col gap-5">
          <section className="flex flex-col gap-2">
            <p className="text-sm text-muted">
              {t('clients.since', { date: formatDateShort(dateOf(c.createdAt, SALON_TZ), locale) })}
              {c.noShows > 0 && <span className="text-danger"> · {t('appt.noShows', { n: c.noShows })}</span>}
              {c.lateCancels > 0 && <span className="text-warning"> · {t('clients.lateCancels', { n: c.lateCancels })}</span>}
            </p>
            {c.bookingBlocked && <p className="self-start rounded-full bg-danger-soft px-2.5 py-0.5 text-xs font-semibold text-danger">{t('clients.blocked')}</p>}
            {c.phone && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums">{formatPhone(c.phone)}</span>
                <a className="rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle" href={`tel:${c.phone}`}>
                  {t('appt.call')}
                </a>
                <a className="rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle" href={`https://wa.me/${c.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
                  {t('appt.whatsapp')}
                </a>
              </div>
            )}
            {c.email && <p className="text-muted">{c.email}</p>}
            {c.notes && <p className="whitespace-pre-line rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">{c.notes}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/s/$orgId/agenda"
                params={{ orgId }}
                search={{ nieuw: 1, klant: c.id }}
                className="inline-flex min-h-11 items-center rounded-full bg-accent px-4 text-[15px] font-semibold text-on-accent hover:bg-accent-strong"
              >
                {t('clients.book')}
              </Link>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {t('appt.edit')}
              </Button>
              {c.email && !c.appointments.some((a) => a.active && Date.parse(a.startsAt) > Date.now()) && c.appointments.some((a) => a.active && Date.parse(a.startsAt) <= Date.now()) && (
                <RemindButton orgId={orgId} clientId={c.id} />
              )}
            </div>
          </section>

          <AlertNote orgId={orgId} client={c} onSaved={refresh} />

          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-sm font-semibold">{t('clients.history')}</h3>
            {c.appointments.length === 0 && <p className="text-sm text-muted">{t('clients.noAppointments')}</p>}
            <ul className="flex flex-col">
              {c.appointments.map((a) => (
                <li key={a.id}>
                  <Link
                    to="/s/$orgId/agenda"
                    params={{ orgId }}
                    search={{ datum: dateOf(a.startsAt, SALON_TZ), afspraak: a.id }}
                    className={`flex items-center justify-between gap-3 rounded-[var(--radius-control)] px-2 py-2 hover:bg-subtle ${a.active ? '' : 'text-muted'}`}
                  >
                    <span>
                      <span className="font-medium tabular-nums">
                        {formatDateShort(dateOf(a.startsAt, SALON_TZ), locale)} {formatTime(a.startsAt, SALON_TZ)}
                      </span>{' '}
                      · {a.service}
                    </span>
                    {!a.active && <span className="text-xs font-semibold">{t(`status.${a.status}` as TKey)}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {c.messages.length > 0 && (
            <section className="flex flex-col gap-2 border-t border-line pt-4">
              <h3 className="text-sm font-semibold">{t('clients.messages')}</h3>
              <ul className="flex flex-col gap-1 text-sm">
                {c.messages.map((m, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>
                      {t(`msg.${m.kind}` as TKey)} <span className="text-muted">· {formatStamp(m.sentAt, SALON_TZ, locale)}</span>
                    </span>
                    <span className={m.status === 'sent' ? 'text-muted' : 'font-semibold text-danger'}>{m.status === 'sent' ? t('clients.msgSent') : t('clients.msgFailed')}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {manager && (
            <section className="flex flex-col gap-3 border-t border-line pt-4">
              <Switch label={t('clients.blockLabel')} hint={t('clients.blockHint')} checked={c.bookingBlocked} onChange={(v) => block.mutate(v)} />
              <ErrorNote message={errorOf(block.error ?? anonymise.error, t('error.generic'))} />
              <button
                type="button"
                className="self-start text-sm font-semibold text-danger hover:underline"
                onClick={() => window.confirm(t('clients.anonymiseConfirm', { name: fullName(c) })) && anonymise.mutate()}
              >
                {t('clients.anonymise')}
              </button>
            </section>
          )}
        </div>
      )}
    </>
  );
}

/** "Let op": encrypted; health information only with the client's consent. */
function AlertNote({ orgId, client, onSaved }: { orgId: string; client: ClientDetail; onSaved: (c: ClientDetail) => void }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(client.alertNote ?? '');
  const [health, setHealth] = useState(client.alertHealth);
  const [consent, setConsent] = useState(client.alertHealth);
  const save = useMutation({
    mutationFn: () => api<{ client: ClientDetail }>(`/orgs/${orgId}/clients/${client.id}/alert`, { method: 'PUT', body: JSON.stringify({ note: note.trim() || null, health, consent }) }).then((r) => r.client),
    onSuccess: (c) => {
      onSaved(c);
      setEditing(false);
    },
  });
  const withdraw = useMutation({
    mutationFn: () => api<{ client: ClientDetail }>(`/orgs/${orgId}/clients/${client.id}/consent/withdraw`, { method: 'POST' }).then((r) => r.client),
    onSuccess: (c) => {
      onSaved(c);
      setNote('');
      setHealth(false);
      setConsent(false);
    },
  });

  if (!editing) {
    return (
      <section className="flex flex-col gap-2 border-t border-line pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{t('cal.alert')}</h3>
          <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={() => setEditing(true)}>
            {client.alertNote ? t('appt.edit') : t('clients.alertAdd')}
          </button>
        </div>
        {client.alertNote ? (
          <>
            <p className="rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-sm text-warning">⚠ {client.alertNote}</p>
            {client.alertHealth && (
              <p className="text-xs text-muted">
                {t('clients.consentGiven')}{' '}
                <button type="button" className="font-semibold text-danger hover:underline" onClick={() => window.confirm(t('clients.withdrawConfirm')) && withdraw.mutate()}>
                  {t('clients.withdraw')}
                </button>
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">{t('clients.alertNone')}</p>
        )}
      </section>
    );
  }
  const needsConsent = health && !!note.trim();
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-4">
      <TextArea id="alert" label={t('cal.alert')} placeholder={t('clients.alertPlaceholder')} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-0.5 size-5 accent-[var(--color-accent)]" checked={health} onChange={(e) => setHealth(e.target.checked)} />
        <span>
          {t('clients.health')}
          <span className="block text-sm text-muted">{t('clients.healthHint')}</span>
        </span>
      </label>
      {needsConsent && (
        <label className="flex items-start gap-2 rounded-[var(--radius-control)] bg-warning-soft px-3 py-2">
          <input type="checkbox" className="mt-0.5 size-5 accent-[var(--color-accent)]" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span className="text-sm">{t('clients.consent')}</span>
        </label>
      )}
      <ErrorNote message={errorOf(save.error, t('error.generic'))} />
      <div className="flex gap-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending || (needsConsent && !consent)}>
          {t('common.save')}
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </section>
  );
}

function ClientForm({ orgId, client, onSaved, onCancel }: { orgId: string; client?: ClientDetail; onSaved: (c: { id: string } & Partial<ClientDetail>) => void; onCancel: () => void }) {
  const t = useT();
  const [firstName, setFirstName] = useState(client?.firstName ?? '');
  const [lastName, setLastName] = useState(client?.lastName ?? '');
  const [phone, setPhone] = useState(client?.phone ? formatPhone(client.phone) : '');
  const [email, setEmail] = useState(client?.email ?? '');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({ firstName, lastName, phone, email, notes });
      return client
        ? api<{ client: ClientDetail }>(`/orgs/${orgId}/clients/${client.id}`, { method: 'PUT', body }).then((r) => r.client)
        : api<{ customer: { id: string } }>(`/orgs/${orgId}/customers`, { method: 'POST', body }).then((r) => r.customer);
    },
    onSuccess: onSaved,
  });
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field id="c-first" label={t('appt.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        <Field id="c-last" label={t('appt.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <Field id="c-phone" type="tel" label={t('appt.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
      <Field id="c-email" type="email" label={t('field.email')} value={email} onChange={(e) => setEmail(e.target.value)} />
      <TextArea id="c-notes" label={t('clients.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <ErrorNote message={errorOf(save.error, t('error.generic'))} />
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

/** Initials in a soft circle, like a contact card. */
export function Initials({ name, size = 'md' }: { name: string; size?: 'md' | 'lg' }) {
  const letters = name.split(/\s+/).filter(Boolean).map((w) => w[0]).filter((ch) => ch && ch === ch.toUpperCase()).slice(0, 2).join('') || name.slice(0, 1).toUpperCase();
  return (
    <span aria-hidden className={`flex shrink-0 items-center justify-center rounded-full bg-accent-soft font-semibold text-accent-strong ${size === 'lg' ? 'size-11 text-[14px]' : 'size-9 text-[12px]'}`}>
      {letters}
    </span>
  );
}
